import { createStore, createEffect, createEvent, combine } from 'effector';
import i18n from 'i18n';
import { $notificationStore } from '../notificationStore';
import { $integrationsStore } from './integrationsStore';
import { ApiUtils } from '@shared/utils';
import { IntegrationsApi } from '@shared/api/rest';
import { type IQueryParams, RequestStrategyEnum } from '@shared/api/models';
import { type ConfirmConnectRequest, IntegrationStep, IntegrationType } from '@shared/api/rest/integrations';

const connectEffect = createEffect((params: IQueryParams<IntegrationType>) => IntegrationsApi.connect(params.request, params?.signal));
connectEffect.done.watch((data) => {
  if (data.result.data?.redirectUrl) {
    window.location.href = data.result.data?.redirectUrl;
  }
});
const disconnectEffect = createEffect((params: IQueryParams<IntegrationType>) => IntegrationsApi.disconnect(params.request, params?.signal));
disconnectEffect.done.watch(() => $integrationsStore.events.update());
const confirmConnectEffect = createEffect((params: IQueryParams<ConfirmConnectRequest>) =>
  IntegrationsApi.confirmConnect(params.request, params?.signal),
);
const clearEvent = createEvent();

const $dataStore = createStore<IntegrationStep>(IntegrationStep.NONE)
  .on(connectEffect.pending, (current, data) => {
    if (data) {
      return IntegrationStep.CONNECT;
    }
    return current;
  })
  .on(confirmConnectEffect.pending, (current, data) => {
    if (data) {
      return IntegrationStep.CONFIRM;
    }
    return current;
  })
  .on(confirmConnectEffect.done, () => {
    return IntegrationStep.CONFIRMED;
  })
  .on(confirmConnectEffect.fail, () => {
    return IntegrationStep.CONNECT;
  })
  .on(disconnectEffect.pending, (current, data) => {
    if (data) {
      return IntegrationStep.DISCONNECT;
    }
    return current;
  })
  .on(disconnectEffect.done, () => {
    return IntegrationStep.NONE;
  })
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(confirmConnectEffect.pending, (_, data) => data)
  .on(confirmConnectEffect.fail, () => false)
  .on(confirmConnectEffect.done, () => false)
  .on(connectEffect.pending, (_, data) => data)
  .on(connectEffect.fail, () => false)
  .on(connectEffect.done, () => false)
  .on(disconnectEffect.pending, (_, data) => data)
  .on(disconnectEffect.fail, () => false)
  .on(disconnectEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(confirmConnectEffect, () => null)
  .on(confirmConnectEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(confirmConnectEffect.done, () => null)
  .on(connectEffect, () => null)
  .on(connectEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(connectEffect.done, () => null)
  .on(disconnectEffect, () => null)
  .on(disconnectEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(disconnectEffect.done, () => null)
  .reset(clearEvent);
$errorStore.watch((x) => $notificationStore.events.setError(x));

export const $connectionStore = {
  events: {
    connect: ApiUtils.wrapperApi(connectEffect, RequestStrategyEnum.Debounce),
    confirmConnect: ApiUtils.wrapperApi(confirmConnectEffect, RequestStrategyEnum.Debounce),
    disconnect: ApiUtils.wrapperApi(disconnectEffect, RequestStrategyEnum.Debounce),
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
